import { BASE_URL } from "@/config";

export interface GPUInfo {
  vendor: string;
  name: string;
  availableNum: number;
  num: number;
  vramAvailablePercentage: number;
  vramAvailablePercentageList?: number[];
}

export interface CPUInfo {
  id: string;
  name: string;
  num: number;
}

export interface TotalResources {
  totalCPUs: number;
  totalMemory: number;
  totalStorage: number;
  gpuInfos: GPUInfo[];
  cpuInfos?: CPUInfo[];
}

export interface StatusResponse {
  connected?: boolean;
  resources: TotalResources;
  version?: string;
}

export const getAllocatableGPUs = (data: any): GPUInfo[] => {
  let gpuMap: Map<string, GPUInfo> = new Map();
  const gpuInfos = data.node.allocatable.gpu_infos;

  if (data.node.allocatable.gpu_infos) {
    const availableGPUPerType = data.node.available.gpu / gpuInfos.length;

    data.node.allocatable.gpu_infos.forEach((gpu: any) => {
      const key = `${gpu.vendor}|${gpu.name}`;
      if (gpuMap.has(key)) {
        const existingGPUInfo = gpuMap.get(key)!;
        existingGPUInfo.num++;
        existingGPUInfo.availableNum += availableGPUPerType;
        existingGPUInfo.vramAvailablePercentageList!.push(
          gpu.vram_available_percentage || gpu.VRAM_available_percentage
        );
      } else {
        gpuMap.set(key, {
          vendor: gpu.vendor,
          name: gpu.name,
          num: 1,
          availableNum: availableGPUPerType,
          vramAvailablePercentage: 0,
          vramAvailablePercentageList: [gpu.vram_available_percentage || gpu.VRAM_available_percentage],
        });
      }
    });

    Array.from(gpuMap.keys()).forEach((key) => {
      const value = gpuMap.get(key);
      if (!value) return;
      value.vramAvailablePercentage =
        value.vramAvailablePercentageList!.reduce((p, c) => p + c, 0) /
        value.num;
    });
  }

  return Array.from(gpuMap.values());
};

interface IProviderStatusResponse {
  cluster: {
    leases: number;
    inventory: {
      available: {
        nodes: Array<{
          name: string;
          allocatable: {
            cpu: number;
            gpu: number;
            gpu_infos: [
              {
                vendor: string;
                vendor_id: string;
                name: string;
                model_id: string;
                interface: string;
                memory_size: string;
              }
            ];
            memory: number;
            storage_ephemeral: number;
          };
          available: {
            cpu: number;
            gpu: number;
            gpu_infos: null;
            memory: number;
            storage_ephemeral: number;
          };
          version: string;
        }>;
      };
    };
  };
  bidengine: {
    orders: number;
  };
  manifest: {
    deployments: number;
  };
  address: string;
}

export const getAllFizzNodesForProvider = async (hostUri: string) => {
  try {
    const statusResponse = await fetch(
      `${BASE_URL}/api/status?hostUri=${encodeURIComponent(hostUri)}`,
      { method: "GET", cache: "no-store", signal: AbortSignal.timeout(20000) }
    );
    const statusResponseJson: IProviderStatusResponse =
      await statusResponse.json();

    const nodes = statusResponseJson.cluster?.inventory?.available?.nodes;

    const nodeDetails =
      nodes?.map((node) => {
        let totalMilliCPUs = node.available.cpu; // in millicores
        let totalMemoryBytes = node.available.memory; // in bytes
        let totalStorageBytes = node.available.storage_ephemeral; // in bytes
        let totalCPUs = totalMilliCPUs / 1000;
        let totalMemory = Number((totalMemoryBytes / 10 ** 9).toFixed(2));
        let totalStorage = Number((totalStorageBytes / 10 ** 9).toFixed(2));
        let gpuInfos: GPUInfo[] = getAllocatableGPUs({ node });

        const resources = {
          totalCPUs,
          totalMemory,
          totalStorage,
          gpuInfos,
        };

        return {
          connected: true,
          name: node.name,
          resources,
          version: node.version,
        };
      }) || [];

    return {
      nodes: nodeDetails,
      status: "Active",
    };
  } catch (error) {
    console.log("error in getting fizz gateway status -> ", error);
    return { nodes: [], status: "Inactive" };
  }
};

export const getAllFizzNodeCountAndStatusForProvider = async (
  hostUri: string
) => {
  try {
    const statusResponse = await fetch(
      `${BASE_URL}/api/status?hostUri=${encodeURIComponent(hostUri)}`,
      { method: "GET", cache: "no-store", signal: AbortSignal.timeout(20000) }
    );
    const statusResponseJson: IProviderStatusResponse | { error: true } =
      await statusResponse.json();

    if ("error" in statusResponseJson && statusResponseJson.error)
      return { nodeCount: 0, connected: false };

    const nodes = (statusResponseJson as IProviderStatusResponse)?.cluster
      ?.inventory?.available?.nodes;

    return { nodeCount: nodes?.length || 0, connected: true };
  } catch (error) {
    console.log("error in getting fizz gateway status -> ", error);
    return { nodeCount: 0, connected: false };
  }
};

export const getFizzNodeStatus = async (
  hostUri: string,
  fizzAddress: string
) => {
  try {
    const statusResponse = await fetch(
      fizzAddress
        ? `${BASE_URL}/api/status?hostUri=${encodeURIComponent(
            hostUri
          )}&fizz=${fizzAddress}`
        : `${BASE_URL}/api/status?hostUri=${encodeURIComponent(hostUri)}`,
      { method: "GET", cache: "no-store", signal: AbortSignal.timeout(20000) }
    );
    const statusResponseJson = await statusResponse.json();

    let totalMilliCPUs = 0; // in millicores
    let totalMemoryBytes = 0; // in bytes
    let totalStorageBytes = 0; // in bytes
    let totalGpus = 0;
    let availableMilliCPUs = 0;
    let availableMemoryBytes = 0;
    let availableStorageBytes = 0;
    let availableCPUs = 0;
    let availableMemory = 0;
    let availableStorage = 0;
    let availableGpus = 0;
    let totalCPUs = 0;
    let totalMemory = 0;
    let totalStorage = 0;
    let gpuInfos: GPUInfo[] = [];
    let connected = false;

    if (statusResponseJson.connected) {
      const inventory = statusResponseJson.node.available;
      const allocatable = statusResponseJson.node.allocatable;
      totalMilliCPUs = allocatable.cpu;
      totalMemoryBytes = allocatable.memory;
      totalStorageBytes = allocatable.storage_ephemeral;
      totalCPUs = totalMilliCPUs / 1000;
      totalMemory = Number((totalMemoryBytes / 10 ** 9).toFixed(2));
      totalStorage = Number((totalStorageBytes / 10 ** 9).toFixed(2));
      totalGpus = allocatable.gpu;
      availableMilliCPUs = inventory.cpu;
      availableMemoryBytes = inventory.memory;
      availableStorageBytes = inventory.storage_ephemeral;
      availableGpus = inventory.gpu;
      availableCPUs = availableMilliCPUs / 1000;
      availableMemory = Number((availableMemoryBytes / 10 ** 9).toFixed(2));
      availableStorage = Number((availableStorageBytes / 10 ** 9).toFixed(2));
      gpuInfos = getAllocatableGPUs(statusResponseJson);

      connected = statusResponseJson.connected;
    }
    const resources = {
      totalCPUs,
      totalMemory,
      totalStorage,
      totalGpus,
      availableGpus,
      availableCPUs,
      availableMemory,
      availableStorage,
      gpuInfos,
    };

    console.log("Total Resources -> ", resources);

    return {
      connected,
      resources,
      upload_speed: statusResponseJson.node?.upload_speed,
      download_speed: statusResponseJson.node?.download_speed,
      version: statusResponseJson.node?.version,
      bandwidth: statusResponseJson.node?.bandwidth,
      os: statusResponseJson.node?.os,
      arch: statusResponseJson.node?.arch,
      cuda_version: statusResponseJson.node?.cuda_version,
      nvidia_driver_version: statusResponseJson.node?.nvidia_driver_version,
    };
  } catch (error) {
    console.log("error in getting fizz status -> ", error);
    const resources = {
      totalCPUs: 0,
      totalMemory: 0,
      totalStorage: 0,
      totalGpus: 0,
      availableGpus: 0,
      availableCPUs: 0,
      availableMemory: 0,
      availableStorage: 0,
      gpuInfos: [],
    };
    const connected = false;
    return {
      connected,
      resources,
      upload_speed: null,
      download_speed: null,
      bandwidth: null,
      os: null,
      arch: null,
      cuda_version: null,
      nvidia_driver_version: null,
    };
  }
};
